export type AgencySeoLocationInput = {
  city?: string | null;
  state?: string | null;
  countyName?: string | null;
  agencyTypeLabel?: string | null;
  agencyName?: string | null;
};

const COUNTY_SUFFIX_RE = /\b(co\.?|county)\b/i;
const STATE_AGENCY_RE = /\bstate\b/i;

function norm(s: string | null | undefined): string {
  return (s ?? '').toString().replace(/\s+/g, ' ').trim();
}

function countyBaseName(countyName: string): string {
  return countyName
    .replace(/\s+county\s*$/i, '')
    .replace(/\s+co\.?\s*$/i, '')
    .trim();
}

/** True when free-text city is really a county/jurisdiction label, not a municipality. */
export function isAgencyCityFieldCountyLike(input: AgencySeoLocationInput): boolean {
  const city = norm(input.city);
  if (!city) return false;
  const county = norm(input.countyName);
  const typeLabel = norm(input.agencyTypeLabel);
  const name = norm(input.agencyName);

  if (COUNTY_SUFFIX_RE.test(city)) return true;
  if (county && countyBaseName(county).toLowerCase() === city.toLowerCase()) return true;
  if (STATE_AGENCY_RE.test(typeLabel) && name && COUNTY_SUFFIX_RE.test(name)) return true;
  return false;
}

/**
 * Public SEO geo phrase — avoids "Jefferson, OR" when city is a county label.
 */
export function formatAgencySeoLocationPhrase(input: AgencySeoLocationInput): string {
  const state = norm(input.state);
  const county = norm(input.countyName);
  const city = norm(input.city);

  if (isAgencyCityFieldCountyLike(input)) {
    const base = county ? countyBaseName(county) || city.replace(/\s+co\.?\s*$/i, '').trim() : city;
    const countyLabel = /county/i.test(county) || /county/i.test(city)
      ? (county || `${base} County`)
      : `${base} County`;
    return state ? `${countyLabel}, ${state}` : countyLabel;
  }

  if (county && city && countyBaseName(county).toLowerCase() !== city.toLowerCase()) {
    return [county, city, state].filter(Boolean).join(', ');
  }
  return [city, state].filter(Boolean).join(', ');
}
